import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
    Document,
    Envelope,
    Signatory,
    SystemEntity,
} from '@wdx/clmi/clmi-swagger-gen';
import { PaginatedApiResponse } from '@wdx/shared/utils';
import { Observable } from 'rxjs';
import * as envelopeActions from './envelope.actions';
import * as envelopeSelectors from './envelope.selectors';

@Injectable({ providedIn: 'root' })
export class EnvelopeFacadeService {
    constructor(private store$: Store) {}

    dispatchGetAll(): void {
        this.store$.dispatch(envelopeActions.getAll());
    }

    dispatchGetForEntityId(
        entityId: string,
        entity: SystemEntity,
        status?: string,
    ): void {
        this.store$.dispatch(
            envelopeActions.getForEntityId({ entityId, entity, status }),
        );
    }

    dispatchGetForId(id: string): void {
        this.store$.dispatch(envelopeActions.getForId({ id }));
    }

    dispatchClearActiveEnvelope(): void {
        this.store$.dispatch(envelopeActions.clearActiveEnvelope());
    }

    dispatchSendForId(id: string): void {
        this.store$.dispatch(envelopeActions.sendForId({ id }));
    }

    dispatchResendForId(id: string): void {
        this.store$.dispatch(envelopeActions.resendForId({ id }));
    }

    dispatchDeleteForId(id: string): void {
        this.store$.dispatch(envelopeActions.deleteForId({ id }));
    }

    dispatchGetDocumentsForId(id: string): void {
        this.store$.dispatch(envelopeActions.getDocumentsForId({ id }));
    }

    dispatchGetSignatoriesForId(id: string): void {
        this.store$.dispatch(envelopeActions.getSignatoriesForId({ id }));
    }

    dispatchAddDocumentForId(envelopeId: string, documentId: string): void {
        this.store$.dispatch(
            envelopeActions.addDocumentForId({ envelopeId, documentId }),
        );
    }

    dispatchAddSignatoryForId(envelopeId: string, partyId: string): void {
        this.store$.dispatch(
            envelopeActions.addSignatoryForId({ envelopeId, partyId }),
        );
    }

    dispatchDeleteDocumentForId(envelopeId: string, documentId: string): void {
        this.store$.dispatch(
            envelopeActions.deleteDocumentForId({ envelopeId, documentId }),
        );
    }

    selectGetAll$(): Observable<PaginatedApiResponse<Envelope>> {
        return this.store$.select(envelopeSelectors.getAll);
    }

    selectGetAllIsLoadingPage$(): Observable<boolean> {
        return this.store$.select(envelopeSelectors.getAllIsLoadingPage);
    }

    selectGetAllHasLoadingPageError$(): Observable<boolean> {
        return this.store$.select(envelopeSelectors.getAllHasLoadingPageError);
    }

    selectGetAllIsUpdating$(): Observable<boolean> {
        return this.store$.select(envelopeSelectors.getAllIsUpdating);
    }

    selectGetAllHasUpdatingError$(): Observable<boolean> {
        return this.store$.select(envelopeSelectors.getAllHasUpdatingError);
    }

    selectGetAllIsDeleting$(): Observable<boolean> {
        return this.store$.select(envelopeSelectors.getAllIsDeleting);
    }

    selectGetAllHasDeletingError$(): Observable<boolean> {
        return this.store$.select(envelopeSelectors.getAllHasDeletingError);
    }

    selectGetForEntityId$(
        entityId: string,
        entity: SystemEntity,
    ): Observable<Envelope[]> {
        return this.store$.select(
            envelopeSelectors.getForEntityId({ entityId, entity }),
        );
    }

    selectGetForEntityIdIsLoadingList$(
        entityId: string,
        entity: SystemEntity,
    ): Observable<boolean> {
        return this.store$.select(
            envelopeSelectors.getForEntityIdIsLoadingList({ entityId, entity }),
        );
    }

    selectGetForEntityIdHasLoadingListError$(
        entityId: string,
        entity: SystemEntity,
    ): Observable<boolean> {
        return this.store$.select(
            envelopeSelectors.getForEntityIdHasLoadingListError({
                entityId,
                entity,
            }),
        );
    }

    selectGetForEntityIdIsUpdating$(
        entityId: string,
        entity: SystemEntity,
    ): Observable<boolean> {
        return this.store$.select(
            envelopeSelectors.getForEntityIdIsUpdating({ entityId, entity }),
        );
    }

    selectGetForEntityIdHasUpdatingError$(
        entityId: string,
        entity: SystemEntity,
    ): Observable<boolean> {
        return this.store$.select(
            envelopeSelectors.getForEntityIdHasUpdatingError({
                entityId,
                entity,
            }),
        );
    }

    selectGetForEntityIdIsDeleting$(
        entityId: string,
        entity: SystemEntity,
    ): Observable<boolean> {
        return this.store$.select(
            envelopeSelectors.getForEntityIdIsDeleting({ entityId, entity }),
        );
    }

    selectGetForEntityIdHasDeletingError$(
        entityId: string,
        entity: SystemEntity,
    ): Observable<boolean> {
        return this.store$.select(
            envelopeSelectors.getForEntityIdHasDeletingError({
                entityId,
                entity,
            }),
        );
    }

    selectGetForId$(id: string): Observable<Envelope> {
        return this.store$.select(envelopeSelectors.getForId({ id }));
    }

    selectGetForIdIsLoadingSingle$(id: string): Observable<boolean> {
        return this.store$.select(
            envelopeSelectors.getForIdIsLoadingSingle({ id }),
        );
    }

    selectGetForIdHasLoadingSingleError$(id: string): Observable<boolean> {
        return this.store$.select(
            envelopeSelectors.getForIdHasLoadingSingleError({ id }),
        );
    }

    selectGetForIdIsUpdating$(id: string): Observable<boolean> {
        return this.store$.select(envelopeSelectors.getForIdIsUpdating({ id }));
    }

    selectGetForIdHasUpdatingError$(id: string): Observable<boolean> {
        return this.store$.select(
            envelopeSelectors.getForIdHasUpdatingError({ id }),
        );
    }

    selectGetForIdIsDeleting$(id: string): Observable<boolean> {
        return this.store$.select(envelopeSelectors.getForIdIsDeleting({ id }));
    }

    selectGetForIdHasDeletingError$(id: string): Observable<boolean> {
        return this.store$.select(
            envelopeSelectors.getForIdHasDeletingError({ id }),
        );
    }

    selectGetActiveEnvelopeId$(): Observable<string> {
        return this.store$.select(envelopeSelectors.getActiveEnvelopeId);
    }

    selectGetActiveEnvelope$(): Observable<Envelope> {
        return this.store$.select(envelopeSelectors.getActiveEnvelope);
    }

    selectGetActiveEnvelopeIsLoading$(): Observable<boolean> {
        return this.store$.select(envelopeSelectors.getActiveEnvelopeIsLoading);
    }

    selectGetActiveEnvelopeHasLoadingSingleError$(): Observable<boolean> {
        return this.store$.select(
            envelopeSelectors.getActiveEnvelopeHasLoadingSingleError,
        );
    }

    selectGetActiveEnvelopeIsUpdating$(): Observable<boolean> {
        return this.store$.select(
            envelopeSelectors.getActiveEnvelopeIsUpdating,
        );
    }

    selectGetActiveEnvelopeHasUpdatingError$(): Observable<boolean> {
        return this.store$.select(
            envelopeSelectors.getActiveEnvelopeHasUpdatingError,
        );
    }

    selectGetActiveEnvelopeIsDeleting$(): Observable<boolean> {
        return this.store$.select(
            envelopeSelectors.getActiveEnvelopeIsDeleting,
        );
    }

    selectGetActiveEnvelopeHasDeletingError$(): Observable<boolean> {
        return this.store$.select(
            envelopeSelectors.getActiveEnvelopeHasDeletingError,
        );
    }

    selectGetDocumentsForId$(id: string): Observable<Document[]> {
        return this.store$.select(envelopeSelectors.getDocumentsForId({ id }));
    }

    selectGetDocumentsForIdIsLoadingList$(id: string): Observable<boolean> {
        return this.store$.select(
            envelopeSelectors.getDocumentsForIdIsLoadingList({ id }),
        );
    }

    selectGetDocumentsForIdHasLoadingListError$(
        id: string,
    ): Observable<boolean> {
        return this.store$.select(
            envelopeSelectors.getDocumentsForIdHasLoadingListError({ id }),
        );
    }

    selectGetDocumentsForIdIsUpdating$(id: string): Observable<boolean> {
        return this.store$.select(
            envelopeSelectors.getDocumentsForIdIsUpdating({ id }),
        );
    }

    selectGetDocumentsForIdHasUpdatingError$(id: string): Observable<boolean> {
        return this.store$.select(
            envelopeSelectors.getDocumentsForIdHasUpdatingError({ id }),
        );
    }

    selectGetDocumentsForIdIsDeleting$(id: string): Observable<boolean> {
        return this.store$.select(
            envelopeSelectors.getDocumentsForIdIsDeleting({ id }),
        );
    }

    selectGetDocumentsForIdHasDeletingError$(id: string): Observable<boolean> {
        return this.store$.select(
            envelopeSelectors.getDocumentsForIdHasDeletingError({ id }),
        );
    }

    selectGetDocumentsForActiveEnvelope$(): Observable<Document[]> {
        return this.store$.select(
            envelopeSelectors.getDocumentsForActiveEnvelope,
        );
    }

    selectGetDocumentsForActiveEnvelopeIsLoadingList$(): Observable<boolean> {
        return this.store$.select(
            envelopeSelectors.getDocumentsForActiveEnvelopeIsLoadingList,
        );
    }

    selectGetDocumentsForActiveEnvelopeHasLoadingListError$(): Observable<boolean> {
        return this.store$.select(
            envelopeSelectors.getDocumentsForActiveEnvelopeHasLoadingListError,
        );
    }

    selectGetDocumentsForActiveEnvelopeIsUpdating$(): Observable<boolean> {
        return this.store$.select(
            envelopeSelectors.getDocumentsForActiveEnvelopeIsUpdating,
        );
    }

    selectGetDocumentsForActiveEnvelopeHasUpdatingError$(): Observable<boolean> {
        return this.store$.select(
            envelopeSelectors.getDocumentsForActiveEnvelopeHasUpdatingError,
        );
    }

    selectGetDocumentsForActiveEnvelopeIsDeleting$(): Observable<boolean> {
        return this.store$.select(
            envelopeSelectors.getDocumentsForActiveEnvelopeIsDeleting,
        );
    }

    selectGetDocumentsForActiveEnvelopeHasDeletingError$(): Observable<boolean> {
        return this.store$.select(
            envelopeSelectors.getDocumentsForActiveEnvelopeHasDeletingError,
        );
    }

    selectGetSignatoriesForId$(id: string): Observable<Signatory[]> {
        return this.store$.select(
            envelopeSelectors.getSignatoriesForId({ id }),
        );
    }

    selectGetSignatoriesForIdIsLoadingList$(id: string): Observable<boolean> {
        return this.store$.select(
            envelopeSelectors.getSignatoriesForIdIsLoadingList({ id }),
        );
    }

    selectGetSignatoriesForIdHasLoadingListError$(
        id: string,
    ): Observable<boolean> {
        return this.store$.select(
            envelopeSelectors.getSignatoriesForIdHasLoadingListError({ id }),
        );
    }
}
